import { Component, Input } from '@angular/core';

/**
 * Stat Card Component
 *
 * Displays a statistic with icon, value, label, and optional trend indicator.
 *
 * Usage:
 * ```html
 * <app-stat-card
 *   icon="users"
 *   value="1,234"
 *   label="Total Users"
 *   [trend]="5.2"
 *   trendLabel="vs last month">
 * </app-stat-card>
 * ```
 */
@Component({
  selector: 'app-stat-card',
  standalone: false,
  templateUrl: './stat-card.component.html',
  styleUrl: './stat-card.component.scss'
})
export class StatCardComponent {
  /** Feather icon name */
  @Input() icon = 'activity';

  /** Icon background color variant */
  @Input() iconColor: 'primary' | 'success' | 'warning' | 'danger' | 'info' | 'secondary' = 'primary';

  /** Main value to display */
  @Input() value: string | number = '0';

  /** Label describing the stat */
  @Input() label = '';

  /** Trend percentage (positive = up, negative = down) */
  @Input() trend: number | null = null;

  /** Trend label (e.g., "vs last month") */
  @Input() trendLabel = '';

  /** Whether the card is loading */
  @Input() loading = false;

  get trendClass(): string {
    if (this.trend === null) return '';
    return this.trend >= 0 ? 'text-success' : 'text-danger';
  }

  get trendIcon(): string {
    if (this.trend === null) return '';
    return this.trend >= 0 ? 'trending-up' : 'trending-down';
  }

  get formattedTrend(): string {
    if (this.trend === null) return '';
    const prefix = this.trend >= 0 ? '+' : '';
    return `${prefix}${this.trend}%`;
  }
}
